import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MessageCircle, Search, Trash2, Pencil } from "lucide-react";
import { Modal, Field } from "@/pages/Agenda";
import { whatsappLink, brl, fmtDateBR } from "@/lib/format";
import { useSearchParams } from "react-router-dom";

export default function Clientes() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    load();
    if (params.get("new") === "1") setShowModal(true);
  // eslint-disable-next-line
  }, []);

  const load = async () => {
    const r = await api.get("/clients", { params: q ? { search: q } : {} });
    setItems(r.data);
  };

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line*/ }, [q]);

  const openDetail = async (id) => {
    const r = await api.get(`/clients/${id}`);
    setDetail(r.data);
  };

  return (
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Clientes</h1>
          <p className="text-[#71717A] text-sm mt-1">{items.length} cadastradas</p>
        </div>
        <button data-testid="btn-new-client" onClick={() => { setEditing(null); setShowModal(true); }} className="btn-primary-nc flex items-center gap-1 text-sm">
          <Plus size={16} /> Nova
        </button>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
        <input data-testid="search-clients" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar cliente…"
          className="w-full h-11 pl-10 pr-3 rounded-xl bg-white border border-[#E4E4E7] focus:border-[#682F3E] outline-none" />
      </div>

      {items.length === 0 ? (
        <div className="card-nc p-8 text-center" data-testid="clients-empty">
          <p className="text-[#71717A] mb-3">Sua primeira cliente vai aparecer aqui.</p>
          <button onClick={() => setShowModal(true)} className="btn-primary-nc">Cadastrar cliente</button>
        </div>
      ) : (
        <div className="grid gap-2">
          {items.map(c => (
            <div key={c.id} data-testid={`client-${c.id}`} className="card-nc p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#EBD8D2] flex items-center justify-center text-[#682F3E] font-bold">
                {c.name.slice(0,1).toUpperCase()}
              </div>
              <button className="flex-1 text-left" onClick={() => openDetail(c.id)} data-testid={`open-client-${c.id}`}>
                <div className="font-semibold text-[#27272A]">{c.name}</div>
                <div className="text-xs text-[#71717A]">{c.phone || c.whatsapp || "—"}</div>
                {c.tags?.length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {c.tags.slice(0,3).map(t => <span key={t} className="badge-status bg-[#F5ECE8] text-[#682F3E]">{t}</span>)}
                  </div>
                )}
              </button>
              {(c.whatsapp || c.phone) && (
                <a data-testid={`wa-${c.id}`} href={whatsappLink(c.whatsapp || c.phone, `Olá, ${c.name}!`)} target="_blank" rel="noreferrer"
                  className="text-[#4F7A61] p-2"><MessageCircle size={18} /></a>
              )}
              <button data-testid={`edit-${c.id}`} onClick={() => { setEditing(c); setShowModal(true); }} className="text-[#71717A] p-2"><Pencil size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ClientModal initial={editing} onClose={() => setShowModal(false)}
          onSaved={async () => { setShowModal(false); await load(); toast.success("Cliente salva!"); }} />
      )}
      {detail && <ClientDetail data={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ClientModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || "", phone: initial?.phone || "", whatsapp: initial?.whatsapp || "",
    email: initial?.email || "", birthdate: initial?.birthdate || "",
    instagram: initial?.instagram || "", notes: initial?.notes || "",
    tags: initial?.tags || [], allergies: initial?.allergies || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleTag = (t) => setForm(f => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter(x => x !== t) : [...f.tags, t] }));
  const suggested = ["VIP", "Nova", "Manutenção", "Alongamento", "Inativa"];

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome."); return; }
    setLoading(true);
    try {
      if (initial) await api.patch(`/clients/${initial.id}`, form);
      else await api.post("/clients", form);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const remove = async () => {
    if (!window.confirm("Excluir esta cliente?")) return;
    await api.delete(`/clients/${initial.id}`);
    onSaved();
  };

  return (
    <Modal onClose={onClose} title={initial ? "Editar cliente" : "Nova cliente"} testid="modal-client">
      <Field label="Nome*"><input data-testid="client-name" value={form.name} onChange={set("name")} className="input-nc" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Telefone"><input data-testid="client-phone" value={form.phone} onChange={set("phone")} className="input-nc" /></Field>
        <Field label="WhatsApp"><input data-testid="client-wa" value={form.whatsapp} onChange={set("whatsapp")} className="input-nc" placeholder="5511..." /></Field>
      </div>
      <Field label="E-mail"><input data-testid="client-email" value={form.email} onChange={set("email")} className="input-nc" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nascimento"><input data-testid="client-birth" type="date" value={form.birthdate || ""} onChange={set("birthdate")} className="input-nc" /></Field>
        <Field label="Instagram"><input data-testid="client-ig" value={form.instagram} onChange={set("instagram")} className="input-nc" /></Field>
      </div>
      <Field label="Tags">
        <div className="flex flex-wrap gap-2">
          {suggested.map(t => (
            <button key={t} type="button" onClick={() => toggleTag(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.tags.includes(t) ? "bg-[#682F3E] text-white border-[#682F3E]" : "bg-white text-[#71717A] border-[#E4E4E7]"}`}>
              {t}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Alergias / observações">
        <textarea data-testid="client-notes" rows={2} value={form.notes} onChange={set("notes")} className="input-nc" />
      </Field>
      <div className="flex gap-2">
        {initial && <button onClick={remove} data-testid="client-delete" className="text-[#B76E56] p-3 hover:bg-[#F9ECE8] rounded-xl"><Trash2 size={18} /></button>}
        <button data-testid="client-save" onClick={save} disabled={loading} className="btn-primary-nc flex-1">
          {loading ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}

function ClientDetail({ data, onClose }) {
  const [showAnam, setShowAnam] = useState(false);
  return (
    <Modal onClose={onClose} title={data.name} testid="modal-client-detail">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Atendimentos" value={data.total_atendimentos} />
        <Stat label="Total" value={brl(data.total_gasto)} />
        <Stat label="Ticket" value={brl(data.ticket_medio)} />
      </div>
      <div className="text-xs text-[#71717A]">Último atendimento: {fmtDateBR(data.ultimo_atendimento)}</div>
      <button onClick={() => setShowAnam(true)} data-testid="btn-anamnesis"
        className="w-full py-2.5 rounded-xl border-2 border-[#682F3E] text-[#682F3E] font-semibold text-sm">
        {data.anamnesis ? "Ver / editar anamnese" : "Preencher anamnese"}
      </button>
      <div className="divider-soft" />
      <h4 className="font-semibold text-sm text-[#27272A]">Histórico</h4>
      {data.appointments?.length ? (
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {data.appointments.map(a => (
            <div key={a.id} className="flex items-center justify-between p-2 bg-[#FAF8F6] rounded-lg text-sm">
              <div>
                <div className="font-medium text-[#27272A]">{fmtDateBR(a.date)}</div>
                <div className="text-xs text-[#71717A]">{a.status}</div>
              </div>
              <div className="font-mono-brand text-sm">{brl(a.final_price ?? a.price)}</div>
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-[#71717A]">Sem atendimentos ainda.</p>}
      {showAnam && <AnamnesisFillModal clientId={data.id} initial={data.anamnesis} onClose={() => setShowAnam(false)} />}
    </Modal>
  );
}

function AnamnesisFillModal({ clientId, initial, onClose }) {
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState(initial?.answers || {});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/anamnesis/template").then(r => setTemplate(r.data));
  }, []);

  const setA = (qid, v) => setAnswers(a => ({ ...a, [qid]: v }));

  const save = async () => {
    if (!template) return;
    setLoading(true);
    try {
      await api.post("/anamnesis/response", {
        client_id: clientId, template_id: template.id, answers,
      });
      toast.success("Anamnese salva!");
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  if (!template) return (
    <Modal onClose={onClose} title="Anamnese" testid="modal-anamnesis-fill">
      <div className="text-[#71717A]">Carregando…</div>
    </Modal>
  );

  return (
    <Modal onClose={onClose} title="Anamnese" testid="modal-anamnesis-fill">
      {template.questions.map(q => (
        <div key={q.id} data-testid={`af-${q.id}`}>
          <label className="block text-sm font-semibold text-[#27272A] mb-1">
            {q.label} {q.required && <span className="text-[#B76E56]">*</span>}
          </label>
          {q.type === "text" && <input value={answers[q.id] || ""} onChange={e => setA(q.id, e.target.value)} className="input-nc" />}
          {q.type === "textarea" && <textarea rows={2} value={answers[q.id] || ""} onChange={e => setA(q.id, e.target.value)} className="input-nc" />}
          {q.type === "yes_no" && (
            <div className="grid grid-cols-2 gap-2">
              {["Sim", "Não"].map(o => (
                <button key={o} type="button" onClick={() => setA(q.id, o)}
                  className={`p-2 rounded-xl border-2 text-sm font-semibold ${answers[q.id] === o ? "border-[#682F3E] bg-[#F5ECE8] text-[#682F3E]" : "border-[#E4E4E7] text-[#71717A]"}`}>
                  {o}
                </button>
              ))}
            </div>
          )}
          {q.type === "single_choice" && (
            <div className="space-y-1.5">
              {(q.options || []).map(o => (
                <button key={o} type="button" onClick={() => setA(q.id, o)}
                  className={`w-full text-left p-2 rounded-xl border-2 text-sm ${answers[q.id] === o ? "border-[#682F3E] bg-[#F5ECE8] text-[#682F3E] font-semibold" : "border-[#E4E4E7] text-[#71717A]"}`}>
                  {o}
                </button>
              ))}
            </div>
          )}
          {q.type === "multi_choice" && (
            <div className="space-y-1.5">
              {(q.options || []).map(o => {
                const arr = answers[q.id] || [];
                const on = arr.includes(o);
                return (
                  <button key={o} type="button" onClick={() => setA(q.id, on ? arr.filter(x => x !== o) : [...arr, o])}
                    className={`w-full text-left p-2 rounded-xl border-2 text-sm ${on ? "border-[#682F3E] bg-[#F5ECE8] text-[#682F3E] font-semibold" : "border-[#E4E4E7] text-[#71717A]"}`}>
                    {o}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
      <button onClick={save} disabled={loading} data-testid="af-save" className="btn-primary-nc w-full">
        {loading ? "Salvando…" : "Salvar anamnese"}
      </button>
    </Modal>
  );
}
function Stat({ label, value }) {
  return (
    <div className="p-2.5 bg-[#F5ECE8] rounded-xl text-center">
      <div className="text-[10px] font-semibold uppercase text-[#682F3E]">{label}</div>
      <div className="font-mono-brand text-sm font-bold text-[#27272A]">{value}</div>
    </div>
  );
}
